import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, LogOut, RefreshCw, ShieldCheck, Phone, Mail } from "lucide-react";

const KEY_STORAGE = "ls-admin-key";

const STATUS_OPTIONS = [
  { value: "new", label: "New" },
  { value: "contacted", label: "Contacted" },
  { value: "in_progress", label: "In Progress" },
  { value: "recovered", label: "Recovered" },
  { value: "closed", label: "Closed" },
];

const statusColors = {
  new: "bg-[#D4AF37]/20 text-[#0A192F] border-[#D4AF37]",
  contacted: "bg-[#1E3A5F]/15 text-[#1E3A5F] border-[#1E3A5F]/30",
  in_progress: "bg-blue-100 text-blue-900 border-blue-300",
  recovered: "bg-green-100 text-green-900 border-green-300",
  closed: "bg-gray-200 text-gray-700 border-gray-300",
};

export default function Admin() {
  const [key, setKey] = useState(() => localStorage.getItem(KEY_STORAGE) || "");
  const [authed, setAuthed] = useState(false);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [authLoading, setAuthLoading] = useState(false);

  useEffect(() => {
    if (key) tryAuth(key, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const tryAuth = async (k, silent = false) => {
    setAuthLoading(true);
    try {
      await api.post("/admin/login", { key: k });
      localStorage.setItem(KEY_STORAGE, k);
      setAuthed(true);
      await fetchLeads(k);
    } catch (e) {
      if (!silent) toast.error("Invalid passcode");
      localStorage.removeItem(KEY_STORAGE);
      setAuthed(false);
    } finally {
      setAuthLoading(false);
    }
  };

  const fetchLeads = async (k = key) => {
    setLoading(true);
    try {
      const res = await api.get("/admin/leads", {
        headers: { "X-Admin-Key": k },
      });
      setLeads(res.data);
    } catch (e) {
      toast.error("Failed to load leads");
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (id, status) => {
    try {
      await api.patch(
        `/admin/leads/${id}`,
        { status },
        { headers: { "X-Admin-Key": key } }
      );
      setLeads((prev) => prev.map((l) => (l.id === id ? { ...l, status } : l)));
      toast.success("Status updated");
    } catch {
      toast.error("Update failed");
    }
  };

  const logout = () => {
    localStorage.removeItem(KEY_STORAGE);
    setAuthed(false);
    setKey("");
    setLeads([]);
  };

  if (!authed) {
    return (
      <main className="min-h-screen bg-[#F3F0E6] flex items-center justify-center p-6">
        <div className="w-full max-w-md bg-[#FDFBF7] border border-[#E2DCD0] p-10 rounded-sm">
          <div className="flex items-center gap-3 mb-8">
            <span className="inline-flex h-10 w-10 items-center justify-center rounded-sm bg-[#0A192F] text-[#D4AF37] font-serif-display text-2xl">
              ★
            </span>
            <div>
              <div className="font-serif-display text-xl text-[#0A192F]">Lone Star Admin</div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-[#1E3A5F]">Restricted Access</div>
            </div>
          </div>

          <h1 className="font-serif-display text-3xl text-[#0A192F] mb-2">
            Enter passcode
          </h1>
          <p className="text-sm text-[#1E3A5F] mb-8">
            Only authorized staff can view submitted leads.
          </p>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              tryAuth(key);
            }}
            className="space-y-5"
            data-testid="admin-login-form"
          >
            <div>
              <Label htmlFor="passcode" className="text-xs uppercase tracking-[0.18em] text-[#1E3A5F]">
                Passcode
              </Label>
              <Input
                id="passcode"
                type="password"
                data-testid="admin-passcode-input"
                value={key}
                onChange={(e) => setKey(e.target.value)}
                placeholder="••••••••"
                className="mt-2 h-12 bg-[#FDFBF7] border-[#E2DCD0] rounded-sm focus-visible:ring-[#D4AF37] focus-visible:ring-offset-0"
              />
            </div>
            <Button
              type="submit"
              data-testid="admin-login-btn"
              disabled={authLoading || !key}
              className="w-full bg-[#0A192F] hover:bg-[#112B4F] text-[#FDFBF7] rounded-sm py-6"
            >
              {authLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Unlock Dashboard"}
            </Button>
          </form>

          <div className="mt-8 flex items-center gap-2 text-xs text-[#1E3A5F]">
            <ShieldCheck className="h-4 w-4 text-[#D4AF37]" />
            <span>Default passcode set via backend env (ADMIN_KEY)</span>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-[#FDFBF7]">
      <header className="bg-[#0A192F] text-[#FDFBF7]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="inline-flex h-10 w-10 items-center justify-center rounded-sm bg-[#FDFBF7] text-[#0A192F] font-serif-display text-2xl">
              ★
            </span>
            <div>
              <div className="font-serif-display text-xl">Lone Star Admin</div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-[#E2DCD0]">
                Lead Dashboard
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button
              onClick={() => fetchLeads()}
              data-testid="refresh-btn"
              variant="ghost"
              className="text-[#FDFBF7] hover:bg-[#FDFBF7]/10"
              disabled={loading}
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </Button>
            <Button
              onClick={logout}
              data-testid="logout-btn"
              variant="ghost"
              className="text-[#FDFBF7] hover:bg-[#FDFBF7]/10"
            >
              <LogOut className="h-4 w-4 mr-2" /> Sign out
            </Button>
          </div>
        </div>
      </header>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex items-end justify-between mb-8">
          <div>
            <h1 className="font-serif-display text-4xl text-[#0A192F]">Submitted Leads</h1>
            <p className="text-[#1E3A5F] mt-1 text-sm">
              {leads.length} {leads.length === 1 ? "submission" : "submissions"} on file
            </p>
          </div>
        </div>

        <div className="border border-[#E2DCD0] rounded-sm bg-[#FDFBF7] overflow-hidden">
          <Table data-testid="leads-table">
            <TableHeader>
              <TableRow className="bg-[#F3F0E6]">
                <TableHead className="text-[#0A192F] font-semibold">Name</TableHead>
                <TableHead className="text-[#0A192F] font-semibold">Contact</TableHead>
                <TableHead className="text-[#0A192F] font-semibold">Property</TableHead>
                <TableHead className="text-[#0A192F] font-semibold">Submitted</TableHead>
                <TableHead className="text-[#0A192F] font-semibold">Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {leads.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-16 text-[#1E3A5F]">
                    {loading ? "Loading…" : "No leads yet. They'll appear here when the form is submitted."}
                  </TableCell>
                </TableRow>
              ) : (
                leads.map((l) => (
                  <TableRow key={l.id} data-testid={`lead-row-${l.id}`}>
                    <TableCell>
                      <div className="font-serif-display text-lg text-[#0A192F]">{l.full_name}</div>
                      {l.message && (
                        <div className="text-xs text-[#1E3A5F] mt-1 max-w-xs italic">
                          "{l.message.length > 80 ? l.message.slice(0, 80) + "…" : l.message}"
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col gap-1 text-sm">
                        <a href={`mailto:${l.email}`} className="flex items-center gap-2 text-[#1E3A5F] hover:text-[#0A192F]">
                          <Mail className="h-3 w-3" /> {l.email}
                        </a>
                        <a href={`tel:${l.phone}`} className="flex items-center gap-2 text-[#1E3A5F] hover:text-[#0A192F]">
                          <Phone className="h-3 w-3" /> {l.phone}
                        </a>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm text-[#0A192F]">{l.address}</div>
                      <div className="text-xs text-[#1E3A5F]">
                        {[l.county, l.state].filter(Boolean).join(", ") || "—"}
                      </div>
                    </TableCell>
                    <TableCell className="text-sm text-[#1E3A5F]">
                      {new Date(l.created_at).toLocaleString()}
                    </TableCell>
                    <TableCell>
                      <Select
                        value={l.status}
                        onValueChange={(v) => updateStatus(l.id, v)}
                      >
                        <SelectTrigger
                          data-testid={`status-select-${l.id}`}
                          className="w-[150px] border-[#E2DCD0] rounded-sm"
                        >
                          <SelectValue>
                            <Badge className={`${statusColors[l.status]} border rounded-sm font-medium`}>
                              {STATUS_OPTIONS.find((s) => s.value === l.status)?.label || l.status}
                            </Badge>
                          </SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                          {STATUS_OPTIONS.map((s) => (
                            <SelectItem key={s.value} value={s.value}>
                              {s.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </section>
    </main>
  );
}
